import { Container, Graphics } from 'pixi.js';
import { mulberry32 } from '../sim/rng';
import type { Rect, Track } from '../sim/track';

/**
 * Greybox art for a track, drawn once (render-only; may use any maths).
 * Layer order: city blocks → barrier → run-off → car park → kerbs → tarmac
 * → start line. The polyline strokes use round joins so the drawn edges
 * match the sim's distance-to-centre-line collision exactly.
 */

const COL = {
  ground: 0x6e5a42,
  block: [0x7d6a50, 0x8a7558, 0x6a5640, 0x947c5c],
  roof: 0x5a4834,
  barrier: 0xc8c6bc,
  dust: 0xc9a66b,
  carpark: 0x8d8c86,
  bay: 0xe8e6dc,
  kerbRed: 0xd42a20,
  kerbWhite: 0xf2f0e8,
  tarmac: 0x3a3a3f,
  tarmacEdge: 0x55555a,
  grid: 0xe8e6dc,
};

function strokePath(g: Graphics, track: Track, width: number, color: number): void {
  const s = track.segs;
  g.moveTo(s[0].ax, s[0].ay);
  for (let i = 1; i < s.length; i++) g.lineTo(s[i].ax, s[i].ay);
  g.closePath();
  g.stroke({ width, color, join: 'round', cap: 'round' });
}

interface ThemeArt {
  ground: number;
  scenery: (g: Graphics, track: Track, rnd: () => number) => void;
  /** Run-off band colour; `stripes` paints Paul Ricard-style blue/red bands. */
  runoff: number;
  stripes?: number;
}

const THEMES: Record<string, ThemeArt> = {
  desert: { ground: COL.ground, runoff: COL.dust, scenery: (g, tr, rnd) => blocks(g, tr, rnd, COL.block, COL.roof, 320) },
  riviera: {
    ground: 0x8f8778,
    runoff: 0xb9b2a2,
    scenery: (g, tr, rnd) => {
      harbour(g, tr, rnd);
      blocks(g, tr, rnd, [0xe8d3a8, 0xd9886a, 0xe2b45c, 0xf0c8c0, 0xcfc6b8], 0x9a5a42, 420);
    },
  },
  park: { ground: 0x3e7a34, runoff: 0x5d9e45, scenery: (g, tr, rnd) => trees(g, tr, rnd, [0x2b5e26, 0x2f6a2a, 0x3a7a30], 'round') },
  forest: {
    ground: 0x3a6b30,
    runoff: 0x2f4fae,
    stripes: 0xd23a2a,
    scenery: (g, tr, rnd) => trees(g, tr, rnd, [0x1f4a22, 0x265829, 0x2d6230], 'pine'),
  },
  tropical: { ground: 0x4f9440, runoff: 0x7cbf55, scenery: (g, tr, rnd) => trees(g, tr, rnd, [0x2f7a34, 0x3c8c3a], 'palm') },
};

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Scatter count proportional to the area around the track. */
function scatter(track: Track, perMillion: number): number {
  const b = track.bounds;
  return Math.round((((b.w + 1000) * (b.h + 1000)) / 1e6) * perMillion);
}

function blocks(g: Graphics, track: Track, rnd: () => number, cols: number[], roof: number, perMillion: number): void {
  const b = track.bounds;
  const n = scatter(track, perMillion / 3.5);
  for (let i = 0; i < n; i++) {
    const w = 30 + rnd() * 90;
    const h = 30 + rnd() * 90;
    const x = b.x - 500 + rnd() * (b.w + 1000);
    const y = b.y - 500 + rnd() * (b.h + 1000);
    g.rect(x, y, w, h).fill(cols[Math.floor(rnd() * cols.length)]);
    g.rect(x + 4, y + 4, w - 8, h - 8).fill({ color: roof, alpha: 0.35 });
  }
}

function harbour(g: Graphics, track: Track, rnd: () => number): void {
  // The sea along the bottom of the map, with moored boats.
  const b = track.bounds;
  const top = b.y + b.h * 0.72;
  g.rect(b.x - 600, top, b.w + 1200, b.h + 600).fill(0x2a6fa8);
  for (let i = 0; i < scatter(track, 30); i++) {
    const x = b.x - 500 + rnd() * (b.w + 1000);
    const y = top + 40 + rnd() * (b.h * 0.28 + 400);
    g.roundRect(x, y, 26 + rnd() * 20, 9, 4).fill(0xf4f2ea);
    g.rect(x + 8, y + 2, 8, 5).fill(0x9ec8e0);
  }
}

function trees(g: Graphics, track: Track, rnd: () => number, cols: number[], kind: 'round' | 'pine' | 'palm'): void {
  const b = track.bounds;
  const n = scatter(track, 260);
  for (let i = 0; i < n; i++) {
    const x = b.x - 500 + rnd() * (b.w + 1000);
    const y = b.y - 500 + rnd() * (b.h + 1000);
    const r = 8 + rnd() * 10;
    const c = cols[Math.floor(rnd() * cols.length)];
    if (kind === 'palm') {
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * Math.PI * 2 + rnd();
        g.ellipse(x + Math.cos(a) * r * 0.6, y + Math.sin(a) * r * 0.6, r * 0.7, r * 0.3).fill(c);
      }
      g.circle(x, y, 2.5).fill(0x7a5a2a);
    } else if (kind === 'pine') {
      g.circle(x, y, r).fill(c);
      g.circle(x - r * 0.25, y - r * 0.25, r * 0.5).fill({ color: 0xffffff, alpha: 0.06 });
    } else {
      g.circle(x + 3, y + 3, r).fill({ color: 0x000000, alpha: 0.18 });
      g.circle(x, y, r).fill(c);
    }
  }
}

function grandstand(g: Graphics, track: Track): void {
  // A grandstand outside the start straight.
  const gate = track.gates[0];
  const nx = -gate.uy;
  const ny = gate.ux;
  const off = track.runoffEdge + 34;
  const len = 260;
  const cx = gate.cx - nx * off;
  const cy = gate.cy - ny * off;
  const pts = [
    cx - gate.ux * len / 2 - nx * 22, cy - gate.uy * len / 2 - ny * 22,
    cx + gate.ux * len / 2 - nx * 22, cy + gate.uy * len / 2 - ny * 22,
    cx + gate.ux * len / 2 + nx * 6, cy + gate.uy * len / 2 + ny * 6,
    cx - gate.ux * len / 2 + nx * 6, cy - gate.uy * len / 2 + ny * 6,
  ];
  g.poly(pts).fill(0x8a8f98);
  for (let k = 0; k < 4; k++) {
    const o = -18 + k * 6;
    g.moveTo(cx - gate.ux * (len / 2 - 6) + nx * o, cy - gate.uy * (len / 2 - 6) + ny * o)
      .lineTo(cx + gate.ux * (len / 2 - 6) + nx * o, cy + gate.uy * (len / 2 - 6) + ny * o)
      .stroke({ width: 2, color: k % 2 ? 0xd8d0b0 : 0xb03030 });
  }
}

export function buildTrackArt(track: Track): Container {
  const root = new Container();
  const g = new Graphics();
  root.addChild(g);
  const b = track.bounds;
  const theme = THEMES[track.def.theme] ?? THEMES.desert;
  const rnd = mulberry32(hashStr(track.def.id));

  g.rect(b.x - 600, b.y - 600, b.w + 1200, b.h + 1200).fill(theme.ground);
  theme.scenery(g, track, rnd);

  // Barrier line, run-off band (optionally striped).
  strokePath(g, track, 2 * track.runoffEdge + 6, COL.barrier);
  strokePath(g, track, 2 * track.runoffEdge, theme.runoff);
  if (theme.stripes !== undefined) {
    for (const sg of track.segs) {
      for (let d = 0; d < sg.len; d += 40) {
        const d1 = Math.min(d + 16, sg.len);
        g.moveTo(sg.ax + sg.ux * d, sg.ay + sg.uy * d)
          .lineTo(sg.ax + sg.ux * d1, sg.ay + sg.uy * d1)
          .stroke({ width: 2 * track.runoffEdge, color: theme.stripes, cap: 'butt' });
      }
    }
  }

  // Car parks (drawn over the run-off so the entrances read as open).
  for (const cp of track.carparks) drawCarPark(g, cp, rnd);

  // Kerbs: white band with red blocks along every segment.
  strokePath(g, track, 2 * track.kerbEdge, COL.kerbWhite);
  for (const sg of track.segs) {
    const step = 14;
    for (let d = 0; d < sg.len; d += step * 2) {
      const d1 = Math.min(d + step, sg.len);
      g.moveTo(sg.ax + sg.ux * d, sg.ay + sg.uy * d)
        .lineTo(sg.ax + sg.ux * d1, sg.ay + sg.uy * d1)
        .stroke({ width: 2 * track.kerbEdge, color: COL.kerbRed, cap: 'butt' });
    }
  }

  // Tarmac with a faint edge line.
  strokePath(g, track, 2 * track.halfRoad, COL.tarmacEdge);
  strokePath(g, track, 2 * track.halfRoad - 4, COL.tarmac);

  if (track.def.theme !== 'desert') grandstand(g, track);
  drawStartLine(g, track);
  return root;
}

/** A small vector map of the circuit (for menus and round cards). */
export function trackMapSvg(track: Track, size = 120, stroke = '#ffd84a'): string {
  const b = track.bounds;
  const s = (size - 12) / Math.max(b.w, b.h);
  const ox = (size - b.w * s) / 2 - b.x * s;
  const oy = (size - b.h * s) / 2 - b.y * s;
  const pts = track.segs.map((sg) => `${(sg.ax * s + ox).toFixed(1)},${(sg.ay * s + oy).toFixed(1)}`).join(' ');
  const g = track.gates[0];
  const sx = g.cx * s + ox;
  const sy = g.cy * s + oy;
  return `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" aria-hidden="true">
    <polygon points="${pts}" fill="none" stroke="#000" stroke-width="7" stroke-linejoin="round"/>
    <polygon points="${pts}" fill="none" stroke="${stroke}" stroke-width="3.5" stroke-linejoin="round"/>
    <circle cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" r="4" fill="#fff" stroke="#000" stroke-width="1.5"/>
  </svg>`;
}

function drawCarPark(g: Graphics, cp: Rect & { obstacles: Rect[] }, rnd: () => number): void {
  g.rect(cp.x, cp.y, cp.w, cp.h).fill(COL.carpark);
  // Painted aisle arrows / bay lines.
  for (const o of cp.obstacles) {
    g.rect(o.x - 2, o.y - 2, o.w + 4, o.h + 4).fill({ color: COL.bay, alpha: 0.5 });
    g.rect(o.x, o.y, o.w, o.h).fill(0x76756f);
    // Parked cars, nose-in, in two rows when there is room.
    const carW = 18;
    const carH = 9;
    const cols = Math.max(1, Math.floor(o.w / (carW + 2)));
    for (let row = 0; row * (carH + 3) + carH <= o.h; row++) {
      for (let c = 0; c < cols; c++) {
        const x = o.x + 1 + c * (o.w / cols);
        const y = o.y + 2 + row * (carH + 3);
        const col = [0xb03030, 0x3050a0, 0xd8d0b0, 0x2a2a2a, 0x40804a, 0xc08020][Math.floor(rnd() * 6)];
        const w = Math.min(carW, o.w / cols - 2);
        g.roundRect(x, y, w, carH, 3).fill(col);
        g.rect(x + w * 0.35, y + 1.5, w * 0.3, carH - 3).fill({ color: 0x9ec8e0, alpha: 0.8 });
      }
    }
  }
}

function drawStartLine(g: Graphics, track: Track): void {
  const gate = track.gates[0];
  const nx = -gate.uy;
  const ny = gate.ux;
  const half = track.halfRoad;
  const sq = 6;
  const rows = 2;
  for (let r = 0; r < rows; r++) {
    for (let k = -half; k < half; k += sq) {
      const black = (Math.round((k + half) / sq) + r) % 2 === 0;
      const x0 = gate.cx + nx * k + gate.ux * r * sq;
      const y0 = gate.cy + ny * k + gate.uy * r * sq;
      g.poly([
        x0, y0,
        x0 + nx * sq, y0 + ny * sq,
        x0 + nx * sq + gate.ux * sq, y0 + ny * sq + gate.uy * sq,
        x0 + gate.ux * sq, y0 + gate.uy * sq,
      ]).fill(black ? 0x111111 : 0xf2f2f2);
    }
  }
  // Grid boxes behind the line (6 slots, staggered two-wide).
  for (let slot = 0; slot < 6; slot++) {
    const back = 34 + slot * 26;
    const side = slot % 2 === 0 ? -14 : 14;
    const cx = gate.cx - gate.ux * back + nx * side;
    const cy = gate.cy - gate.uy * back + ny * side;
    const fx = cx + gate.ux * 14;
    const fy = cy + gate.uy * 14;
    g.moveTo(fx + nx * 8, fy + ny * 8).lineTo(fx - nx * 8, fy - ny * 8).stroke({ width: 2, color: COL.grid });
  }
}
